import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage, defineMessages } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

import { Badge, Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';

import { Followers } from '../Followers';
import { Following } from '../Following';
import { Recommended } from '../Recommended';
import { Network_user } from './__generated__/index.graphql';

const tabs = ['recommended', 'followers', 'following'] as const;
type Tab = (typeof tabs)[number];

const messages = defineMessages<Tab>({
  recommended: {
    id: 'Network.recommended',
    defaultMessage: 'Recommended',
  },
  following: {
    id: 'Network.following',
    defaultMessage: '<span>Following</span> <b>{following}</b>',
  },
  followers: {
    id: 'Network.followers',
    defaultMessage: '<span>Followers</span> <b>{followers}</b>',
  },
});

type Props = {
  user: Network_user;
};

export const Network = ({ user }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const [qsParams] = useSearchParams();

  if (!user) return null;
  const actualTabs = tabs.filter(
    t => user.slug === currentUser?.slug || t !== 'recommended'
  );
  const tab = tabs.includes((qsParams.get('tab') as Tab) || '')
    ? `${qsParams.get('tab')}`
    : tabs[0];

  return (
    <div>
      <TabBar value={tab} variant="secondary">
        {actualTabs.map(t => (
          <Tab key={t} value={t} to={`?tab=${t}`}>
            <FormattedMessage
              {...messages[t]}
              values={{
                following: user.followingCount,
                followers: user.followersCount,
                b: (...chunks) => <Badge>{chunks}</Badge>,
                span: (...chunks) => <span>{chunks}</span>,
              }}
            />
          </Tab>
        ))}
      </TabBar>
      {tab === 'recommended' && <Recommended />}
      {tab === 'following' && <Following user={user} />}
      {tab === 'followers' && <Followers user={user} />}
    </div>
  );
};

Network.fragments = {
  user: gql`
    fragment Network_user on PublicUserInfoInterface {
      slug
      followingCount
      followersCount
    }
  ` as TypedDocumentNode<Network_user>,
};

export default Network;
